import { yupResolver } from "@hookform/resolvers/yup";
import { designTokens } from "@itandi/itandi-bb-ui";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormControlText } from "@itandi/itandi-bb-ui/FormControlText";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { Heading } from "@itandi/itandi-bb-ui/Heading";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { Panel } from "@itandi/itandi-bb-ui/Panel";
import { PanelBody } from "@itandi/itandi-bb-ui/PanelBody";
import { PanelHeader } from "@itandi/itandi-bb-ui/PanelHeader";
import { useId, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import useSWRMutation from "swr/mutation";
import type {
	VendorUser,
	VendorUserUpdatePasswordParam
} from "~/models/VendorUser";
import {
	vendorUserPasswordValidationSchema,
	vendorUserValidationScheme
} from "~/models/VendorUser";
import { isSuccess } from "~/utils/api/api_handler";
import { changePassword, updateSelf } from "~/utils/api/vendors/VendorUser";
import { generatePath } from "~/utils/generatePath";

type Props = {
	loginVendorUser: VendorUser;
};

export function ProfileSetting({ loginVendorUser }: Props) {
	const formId = useId();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const handleOpenModal = () => {
		setIsModalOpen(true);
	};
	const handleCloseModal = () => {
		setIsModalOpen(false);
	};

	const {
		register,
		handleSubmit,
		formState: { isDirty, errors }
	} = useForm<VendorUser>({
		defaultValues: loginVendorUser,
		resolver: yupResolver(vendorUserValidationScheme)
	});

	const { trigger: triggerUpdateSelf, isMutating: isMutatingUpdateSelf } =
		useSWRMutation(
			generatePath("/vendors/sessions"),
			async (_key, { arg }: { arg: VendorUser }) => updateSelf(arg)
		);

	const handleSave = async (submitValue: VendorUser): Promise<void> => {
		const result = await triggerUpdateSelf(submitValue);
		if (isSuccess(result)) {
			toast.success("保存しました。");
		} else {
			toast.error(`エラーが発生しました。${result.message ?? ""}`);
		}
	};

	const {
		register: registerPassword,
		handleSubmit: handleSubmitPassword,
		reset: resetPasswordForm,
		formState: { isDirty: isDirtyPassword, errors: errorsPassword }
	} = useForm<VendorUserUpdatePasswordParam>({
		context: { hasPassword: loginVendorUser.hasPassword },
		defaultValues: {
			confirmPassword: "",
			newPassword: "",
			oldPassword: ""
		},
		resolver: yupResolver(vendorUserPasswordValidationSchema)
	});

	const {
		trigger: triggerChangePassword,
		isMutating: isMutatingChangePassword
	} = useSWRMutation(
		generatePath("/vendors/sessions"),
		async (_key, { arg }: { arg: VendorUserUpdatePasswordParam }) =>
			changePassword(arg)
	);

	const handleSavePassword = async (
		submitValue: VendorUserUpdatePasswordParam
	): Promise<void> => {
		const result = await triggerChangePassword(submitValue);
		if (isSuccess(result)) {
			toast.success("パスワードを変更しました。");
			handleCloseModal();
			resetPasswordForm();
		} else {
			toast.error(`エラーが発生しました。${result.message ?? ""}`);
		}
	};

	return (
		<Panel>
			<PanelHeader title="プロフィール詳細" />
			<PanelBody>
				<form noValidate onSubmit={handleSubmit(handleSave)}>
					<Heading as="h3" mb={designTokens["spacing/xs"]}>
						基本情報
					</Heading>
					<FormGroup maxW="32rem" variant="vertical">
						<FormControl chip="必須" isInvalid={!!errors.name} label="氏名">
							<Input {...register("name")} />
							<FormControlText error={errors.name?.message} />
						</FormControl>
						<FormControl
							chip="必須"
							isInvalid={!!errors.email}
							label="メールアドレス"
						>
							<Input {...register("email")} />
							<FormControlText error={errors.email?.message} />
						</FormControl>
						<FormControl chip="必須" isInvalid={!!errors.tel} label="電話番号">
							<Input {...register("tel")} />
							<FormControlText error={errors.tel?.message} />
						</FormControl>
					</FormGroup>
					<Button
						disabled={!isDirty}
						loading={isMutatingUpdateSelf}
						my={designTokens["spacing/m"]}
						type="submit"
						variant="primary"
					>
						保存
					</Button>
				</form>
				<form
					id={formId}
					noValidate
					onSubmit={handleSubmitPassword(handleSavePassword)}
				>
					<Heading as="h3" mb={designTokens["spacing/xs"]}>
						ログイン情報
					</Heading>
					<FormGroup maxW="32rem" variant="vertical">
						{loginVendorUser.hasPassword ? (
							<FormControl
								chip="必須"
								isInvalid={!!errorsPassword.oldPassword}
								label="現在のパスワード"
							>
								<Input {...registerPassword("oldPassword")} type="password" />
								<FormControlText error={errorsPassword.oldPassword?.message} />
							</FormControl>
						) : null}
						<FormControl
							isInvalid={!!errorsPassword.newPassword}
							label="新しいパスワード"
						>
							<Input {...registerPassword("newPassword")} type="password" />
							<FormControlText error={errorsPassword.newPassword?.message} />
						</FormControl>
						<FormControl
							isInvalid={!!errorsPassword.confirmPassword}
							label="新しいパスワード（確認用）"
						>
							<Input {...registerPassword("confirmPassword")} type="password" />
							<FormControlText
								error={errorsPassword.confirmPassword?.message}
							/>
						</FormControl>
					</FormGroup>
					<Button
						disabled={!isDirtyPassword}
						my={designTokens["spacing/m"]}
						onClick={handleSubmitPassword(handleOpenModal)}
						type="button"
						variant="primary"
					>
						パスワードを変更
					</Button>
				</form>
				{isModalOpen ? (
					<Modalv2 isOpen onClose={handleCloseModal} size="s">
						<ModalHeader title="パスワードの変更" />
						<ModalBody>パスワードを変更します。</ModalBody>
						<ModalFooter
							leftArea={
								<Button onClick={handleCloseModal} variant="secondary">
									キャンセル
								</Button>
							}
							rightArea={
								<Button
									form={formId}
									loading={isMutatingChangePassword}
									type="submit"
									variant="primary"
								>
									変更
								</Button>
							}
						/>
					</Modalv2>
				) : null}
			</PanelBody>
		</Panel>
	);
}
